import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api.js';

/**
 * Server state for an app used on mobile data (PLAN §3).
 *
 * The defaults lean towards fewer requests: data stays fresh for half a minute,
 * nothing refetches merely because a window regained focus, and a failed
 * request is retried twice — but never when the server has already given a
 * definite answer, because retrying a 404 or a 401 cannot change it.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry(failureCount, error) {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: {
        // A mutation that failed changed nothing, and the owner is watching —
        // surfacing the error immediately beats a silent retry.
        retry: false,
      },
    },
  });
}

/** Query keys in one place, so an invalidation can never miss a screen. */
export const keys = {
  session: ['session'] as const,
  accounts: ['accounts'] as const,
  platforms: ['platforms'] as const,
  accountGroups: ['account-groups'] as const,
  settings: ['settings'] as const,
  captionRules: ['caption-rules'] as const,
  hashtagSets: ['hashtag-sets'] as const,
  // `folder` is part of the key, not a filter applied after fetching: the
  // library is paged server-side, so a folder's files are a different request
  // rather than a subset of one already in hand.
  media: (kind?: string, folder?: string) => ['media', kind ?? 'all', folder ?? 'any'] as const,
  mediaFolders: ['media', 'folders'] as const,
  mediaItem: (id: string) => ['media', 'item', id] as const,
  posts: (filters: Record<string, unknown> = {}) => ['posts', filters] as const,
  post: (id: string) => ['post', id] as const,
  targetEvents: (id: string) => ['target-events', id] as const,
  calendar: (from: string, to: string) => ['calendar', from, to] as const,
};
